import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { isFeishuEnv } from '@/utils/feishu';

// 导入语言包
import en from './locales/en';
import zh from './locales/zh';

// 支持的语言列表
export const SUPPORTED_LANGUAGES = {
  'en-US': 'English',
  'zh-CN': '简体中文'
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// 默认语言
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en-US';

// 本地存储的key
const LANGUAGE_STORAGE_KEY = 'i18n_language';
const MANUAL_SWITCH_FLAG_KEY = 'i18n_manual_switch';

// 语言代码映射（向后兼容）
const LEGACY_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  'en': 'en-US',
  'zh': 'zh-CN'
};

// 支持通过 URL lang 参数设置语言的认证页面
export const AUTH_PAGE_PATHS = [
  '/login',
  '/signup',
  '/mLogin',
  '/forgot-password',
] as const;

export const isAuthPage = (
  pathname: string = window.location.pathname
): boolean => {
  return AUTH_PAGE_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
};

export const parseUrlLanguageParam = (
  search: string = window.location.search
): SupportedLanguage | null => {
  const lang = new URLSearchParams(search).get('lang')?.trim();
  if (!lang) {
    return null;
  }
  if (lang in SUPPORTED_LANGUAGES) {
    return lang as SupportedLanguage;
  }
  if (lang in LEGACY_LANGUAGE_MAP) {
    return LEGACY_LANGUAGE_MAP[lang];
  }
  return null;
};

export const getAuthPageLanguageFromUrl = (
  pathname: string = window.location.pathname,
  search: string = window.location.search
): SupportedLanguage | null => {
  if (!isAuthPage(pathname)) {
    return null;
  }
  return parseUrlLanguageParam(search);
};

// 根据浏览器语言偏好推断默认语言（中文浏览器 → 中文，否则英文）
export const getBrowserLanguage = (): SupportedLanguage => {
  try {
    const browserLanguages = [
      ...(navigator.languages || []),
      navigator.language,
    ].filter(Boolean);

    for (const lang of browserLanguages) {
      if (lang.toLowerCase().startsWith('zh')) {
        return 'zh-CN';
      }
    }
  } catch (error) {
    console.warn('Failed to detect browser language:', error);
  }
  return DEFAULT_LANGUAGE;
};

// 获取存储的语言设置
export const getStoredLanguage = (): SupportedLanguage => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      // 检查是否是新格式
      if (stored in SUPPORTED_LANGUAGES) {
        return stored as SupportedLanguage;
      }
      // 检查是否是旧格式，进行转换
      if (stored in LEGACY_LANGUAGE_MAP) {
        const newLang = LEGACY_LANGUAGE_MAP[stored];
        // 更新到新格式
        localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
        return newLang;
      }
    }
  } catch (error) {
    console.warn('Failed to read language from localStorage:', error);
  }
  return getBrowserLanguage();
};

// 获取存储的语言设置（如果没有则返回 null）
export const getStoredLanguageOrNull = (): SupportedLanguage | null => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      // 检查是否是新格式
      if (stored in SUPPORTED_LANGUAGES) {
        return stored as SupportedLanguage;
      }
      // 检查是否是旧格式，进行转换
      if (stored in LEGACY_LANGUAGE_MAP) {
        const newLang = LEGACY_LANGUAGE_MAP[stored];
        // 更新到新格式
        localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
        return newLang;
      }
    }
  } catch (error) {
    console.warn('Failed to read language from localStorage:', error);
  }
  return null;
};

// 计算应用初始化时应使用的语言
// 飞书环境下优先使用中文，确保首屏不会先出现英文再切换。
// 无用户手动设置时，根据浏览器语言偏好推断（中文 → zh-CN，否则 en-US）。
export const getInitialLanguage = (): SupportedLanguage => {
  const storedLanguage = getStoredLanguageOrNull();

  if (isFeishuEnv()) {
    if (storedLanguage !== 'zh-CN') {
      setStoredLanguage('zh-CN');
    }
    return 'zh-CN';
  }

  const urlLanguage = getAuthPageLanguageFromUrl();
  if (urlLanguage) {
    setStoredLanguage(urlLanguage);
    return urlLanguage;
  }

  return storedLanguage || getBrowserLanguage();
};

// 设置语言到本地存储
export const setStoredLanguage = (language: SupportedLanguage): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.warn('Failed to save language to localStorage:', error);
  }
};

// 设置手动切换标识
export const setManualSwitchFlag = (): void => {
  try {
    localStorage.setItem(MANUAL_SWITCH_FLAG_KEY, 'true');
  } catch (error) {
    console.warn('Failed to save manual switch flag:', error);
  }
};

// 检查是否是手动切换
export const isManualSwitch = (): boolean => {
  try {
    return localStorage.getItem(MANUAL_SWITCH_FLAG_KEY) === 'true';
  } catch (error) {
    console.warn('Failed to read manual switch flag:', error);
    return false;
  }
};

// 清除手动切换标识
export const clearManualSwitchFlag = (): void => {
  try {
    localStorage.removeItem(MANUAL_SWITCH_FLAG_KEY);
  } catch (error) {
    console.warn('Failed to clear manual switch flag:', error);
  }
};

// 初始化i18n（先设置一个默认语言，然后等待 useLanguageInitialization hook 来管理）
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      'en-US': {
        translation: en
      },
      'zh-CN': {
        translation: zh
      }
    },
    lng: getInitialLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    debug: process.env.NODE_ENV === 'development',
    
    interpolation: {
      escapeValue: false, // React已经安全地转义了
      prefix: '{',
      suffix: '}'
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    }
  });

// 导出初始化语言的函数
export const initializeLanguage = (
  language: SupportedLanguage,
  options: { persist?: boolean } = {}
) => {
  const { persist = false } = options;
  if (language && i18n.language !== language) {
    i18n.changeLanguage(language);
    if (persist) {
      setStoredLanguage(language);
    }
  }
};

export default i18n;
